import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/Card";
import { useToast } from "../hooks/useToast";
import { db } from "../lib/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { SUMMER_2026_SESSIONS, formatCurrency, validatePhone, validateEmail, debounce } from "../lib/utils";
import { calculateRoute, calculatePricingTiers, getPlacePredictions } from "../services/googleMaps";
import type { RouteCalculation, PricingTier } from "@/types";

interface Student {
  id: string;
  grade: string;
  needsCarSeat: boolean;
  needsBooster: boolean;
  needsAssistance: boolean;
}

interface GuardianInfo {
  fullName: string;
  email: string;
  phone: string;
}

// Multi-student discounts
const STUDENT_DISCOUNTS: Record<number, number> = {
  1: 1.0,
  2: 0.95,
  3: 0.90,
  4: 0.85,
  5: 0.80,
  6: 0.75,
};

const DROPOFF_ADDRESS = "8000 Cherokee St, Philadelphia, PA 19118";

export default function CustomQuote() {
  const navigate = useNavigate();
  const { user, profile, loading: authLoading } = useAuth();
  const toast = useToast();

  const [guardianInfo, setGuardianInfo] = useState<GuardianInfo>({
    fullName: "",
    email: "",
    phone: "",
  });

  const [students, setStudents] = useState<Student[]>([
    {
      id: crypto.randomUUID(),
      grade: "",
      needsCarSeat: false,
      needsBooster: false,
      needsAssistance: false,
    },
  ]);

  const [selectedWeeks, setSelectedWeeks] = useState<number[]>([]);
  const [pickupAddress, setPickupAddress] = useState("");
  const [pickupSuggestions, setPickupSuggestions] = useState<Array<{ description: string; place_id: string }>>([]);
  const [showPickupSuggestions, setShowPickupSuggestions] = useState(false);
  const [message, setMessage] = useState("");
  const [interestedInLateShuttle, setInterestedInLateShuttle] = useState(false);
  const [agreedToPrivacyPolicy, setAgreedToPrivacyPolicy] = useState(false);
  const [agreedToMarketing, setAgreedToMarketing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Route calculation state
  const [isCalculating, setIsCalculating] = useState(false);
  const [routeData, setRouteData] = useState<RouteCalculation | null>(null);
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth?redirect=/custom-quote");
    }
  }, [user, authLoading, navigate]);

  // Auto-fill guardian info from user profile if available
  useEffect(() => {
    if (user?.displayName) {
      setGuardianInfo(prev => ({
        ...prev,
        fullName: prev.fullName || user.displayName || "",
      }));
    }
    if (user?.email) {
      setGuardianInfo(prev => ({
        ...prev,
        email: prev.email || user.email || "",
      }));
    }
  }, [user]);

  // Auto-calculate route when pickup address is selected
  useEffect(() => {
    if (pickupAddress && !showPickupSuggestions && !routeData) {
      handleCalculateRoute();
    }
  }, [pickupAddress, showPickupSuggestions]);

  // Debounced address search
  const searchAddresses = useCallback(
    debounce(async (input: string) => {
      if (input.length < 3) {
        setPickupSuggestions([]);
        return;
      }

      try {
        const predictions = await getPlacePredictions(input);
        setPickupSuggestions(predictions);
        setShowPickupSuggestions(true);
      } catch (err) {
        console.error("Failed to get predictions:", err);
      }
    }, 300),
    []
  );

  const handlePickupChange = (value: string) => {
    setPickupAddress(value);
    searchAddresses(value);
    // Reset route data when address changes
    setRouteData(null);
    setPricingTiers([]);
  };

  const selectPickupAddress = (prediction: { description: string; place_id: string }) => {
    setPickupAddress(prediction.description);
    setPickupSuggestions([]);
    setShowPickupSuggestions(false);
  };

  // Calculate route and pricing
  const handleCalculateRoute = async () => {
    if (!pickupAddress) {
      return;
    }

    setIsCalculating(true);
    try {
      const route = await calculateRoute(pickupAddress, DROPOFF_ADDRESS);
      setRouteData(route);

      const tiers = calculatePricingTiers(route);
      setPricingTiers(tiers);
    } catch (err) {
      console.error("Route calculation error:", err);
    } finally {
      setIsCalculating(false);
    }
  };

  // Calculate pricing with student count and weeks
  const calculatePricing = () => {
    const weekCount = selectedWeeks.length;
    const studentCount = students.filter((s) => s.grade).length;

    if (weekCount === 0 || studentCount === 0 || !pricingTiers.length) {
      return null;
    }

    const tier = pricingTiers.find(t => t.students === studentCount) || pricingTiers[0];
    const discount = STUDENT_DISCOUNTS[studentCount] || 0.75;
    const discountPercent = Math.round((1 - discount) * 100);
    const totalRoutePerWeek = tier.pricePerWeek * discount; // Total route cost per week
    const perStudentPerWeek = totalRoutePerWeek / studentCount; // Cost per student per week
    const totalPrice = totalRoutePerWeek * weekCount; // Total for all weeks
    const totalPerStudent = perStudentPerWeek * weekCount; // Total per student for all weeks

    return {
      perStudentPerWeek,
      totalPerStudent,
      totalRoutePerWeek,
      weekCount,
      studentCount,
      discountPercent,
      totalPrice,
    };
  };

  const pricing = calculatePricing();

  // Week selection handlers
  const toggleWeek = (weekNumber: number) => {
    setSelectedWeeks((prev) =>
      prev.includes(weekNumber)
        ? prev.filter((w) => w !== weekNumber)
        : [...prev, weekNumber]
    );
  };

  const selectAllWeeks = () => {
    setSelectedWeeks(SUMMER_2026_SESSIONS.map((_, i) => i + 1));
  };

  const clearWeeks = () => {
    setSelectedWeeks([]);
  };

  // Student management
  const addStudent = () => {
    if (students.length >= 6) {
      toast.warning("Maximum students reached", "You can add up to 6 students per quote.");
      return;
    }
    setStudents([...students, {
      id: crypto.randomUUID(),
      grade: "",
      needsCarSeat: false,
      needsBooster: false,
      needsAssistance: false,
    }]);
  };

  const removeStudent = (id: string) => {
    if (students.length <= 1) return;
    setStudents(students.filter((s) => s.id !== id));
  };

  const updateStudent = (id: string, field: keyof Student, value: string | boolean) => {
    setStudents(students.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
  };

  // Form validation
  const validateForm = () => {
    if (!guardianInfo.fullName) {
      toast.error("Missing information", "Please enter your full name.");
      return false;
    }
    if (!validateEmail(guardianInfo.email)) {
      toast.error("Invalid email", "Please enter a valid email address.");
      return false;
    }
    if (!validatePhone(guardianInfo.phone)) {
      toast.error("Invalid phone", "Please enter a valid phone number.");
      return false;
    }
    if (!pickupAddress) {
      toast.error("Missing address", "Please enter your pickup address.");
      return false;
    }
    if (!routeData) {
      toast.error("Route not calculated", "Please wait for route calculation.");
      return false;
    }
    const validStudents = students.filter((s) => s.grade);
    if (validStudents.length === 0) {
      toast.error("Missing students", "Please add at least one student.");
      return false;
    }
    if (selectedWeeks.length === 0) {
      toast.error("No weeks selected", "Please select at least one week.");
      return false;
    }
    if (!agreedToPrivacyPolicy) {
      toast.error("Privacy Policy", "You must agree to the privacy policy to continue.");
      return false;
    }
    return true;
  };

  // Submit quote request
  const handleSubmit = async () => {
    if (!validateForm() || !pricing || !routeData) return;

    setIsSubmitting(true);

    try {
      const validStudents = students.filter((s) => s.grade);

      // Save to Firestore
      await addDoc(collection(db, "quoteRequests"), {
        userId: user?.uid,
        quoteType: "custom",
        guardianInfo,
        students: validStudents,
        pickupAddress,
        dropoffAddress: DROPOFF_ADDRESS,
        selectedWeeks,
        message: message || "",
        interestedInLateShuttle,
        agreedToPrivacyPolicy,
        agreedToMarketing,
        routeData: {
          distanceMiles: routeData.distanceMiles,
          durationMinutes: routeData.durationMinutes,
        },
        pricing,
        status: "pending",
        createdAt: serverTimestamp(),
      });

      // Send to Zapier for Google Sheets
      try {
        const zapierPayload = {
          timestamp: new Date().toISOString(),
          quoteType: "custom",
          status: "pending",
          guardianName: guardianInfo.fullName,
          guardianEmail: guardianInfo.email,
          guardianPhone: guardianInfo.phone,
          studentCount: validStudents.length,
          camperDetails: validStudents.map((s, i) =>
            `Camper ${i + 1}: Grade ${s.grade}${s.needsCarSeat ? ', Car Seat' : ''}${s.needsBooster ? ', Booster' : ''}${s.needsAssistance ? ', Needs Assistance' : ''}`
          ).join(" | "),
          pickupAddress: pickupAddress,
          dropoffAddress: DROPOFF_ADDRESS,
          hubstopName: "",
          selectedWeeks: selectedWeeks.sort((a, b) => a - b).join(", "),
          weekCount: selectedWeeks.length,
          distanceMiles: routeData.distanceMiles,
          durationMinutes: routeData.durationMinutes,
          pricePerStudentPerWeek: pricing.perStudentPerWeek,
          totalPerStudent: pricing.totalPerStudent,
          totalRoutePerWeek: pricing.totalRoutePerWeek,
          totalPrice: pricing.totalPrice,
          discountPercent: pricing.discountPercent || 0,
          message: message || "",
          interestedInLateShuttle: interestedInLateShuttle ? "Yes" : "No",
          agreedToPrivacyPolicy: agreedToPrivacyPolicy ? "Yes" : "No",
          agreedToMarketing: agreedToMarketing ? "Yes" : "No",
          userReferralCode: (profile as any)?.referralCode || "",
          referredBy: (profile as any)?.referredBy || "",
        };

        console.log("Sending to Zapier webhook:", zapierPayload);
        const response = await fetch("https://hooks.zapier.com/hooks/catch/22972432/uwwui7o/", {
          method: "POST",
          body: JSON.stringify(zapierPayload),
        });
        console.log("Zapier webhook response:", response.status, response.statusText);
        const responseData = await response.json();
        console.log("Zapier webhook response data:", responseData);
      } catch (zapierError) {
        // Don't fail the submission if Zapier fails
        console.error("Zapier webhook error:", zapierError);
      }

      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error("Submission failed", "Please try again or contact support.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-shuttlebee-yellow"></div>
      </div>
    );
  }

  if (submitted) {
    const validStudents = students.filter((s) => s.grade);

    return (
      <div className="min-h-screen bg-gradient-to-br from-shuttlebee-dark via-shuttlebee-navy to-shuttlebee-blue flex items-center justify-center p-4">
        <Card className="max-w-3xl w-full">
          <CardContent className="pt-8 pb-8">
            {/* Success Header */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">✅</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Quote Submitted!</h1>
              <p className="text-gray-600">
                We've sent your personalized quote to <strong>{guardianInfo.email}</strong>
              </p>
            </div>

            {/* Quote Summary */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Quote Summary</h2>

              {/* Guardian Information */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">Guardian Information</h3>
                <div className="space-y-1 text-sm text-gray-700">
                  <p><strong>Name:</strong> {guardianInfo.fullName}</p>
                  <p><strong>Email:</strong> {guardianInfo.email}</p>
                  <p><strong>Phone:</strong> {guardianInfo.phone}</p>
                </div>
              </div>

              {/* Campers */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">Campers ({validStudents.length})</h3>
                <div className="space-y-2">
                  {validStudents.map((student, idx) => (
                    <div key={student.id} className="text-sm text-gray-700 bg-white rounded p-3">
                      <p className="font-medium">Camper {idx + 1}</p>
                      <p>Grade: {student.grade}</p>
                      {(student.needsCarSeat || student.needsBooster || student.needsAssistance) && (
                        <p className="text-gray-600 mt-1">
                          {student.needsCarSeat && '🚗 Car Seat '}
                          {student.needsBooster && '💺 Booster '}
                          {student.needsAssistance && '🤝 Assistance Needed'}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Route Details */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">Route Details</h3>
                <div className="space-y-2 text-sm text-gray-700">
                  <div className="bg-white rounded p-3">
                    <p className="font-medium text-gray-900 mb-1">📍 Pickup</p>
                    <p>{pickupAddress}</p>
                  </div>
                  <div className="bg-white rounded p-3">
                    <p className="font-medium text-gray-900 mb-1">🎯 Destination</p>
                    <p>Summerside Camps at SCH</p>
                    <p className="text-xs text-gray-600">{DROPOFF_ADDRESS}</p>
                  </div>
                  {routeData && (
                    <div className="bg-white rounded p-3 flex justify-between items-center">
                      <div>
                        <p className="text-gray-600">Distance</p>
                        <p className="font-semibold">{routeData.distanceMiles.toFixed(1)} miles</p>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-600">Duration</p>
                        <p className="font-semibold">{routeData.durationMinutes} min</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Weeks Selected */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Weeks Selected ({selectedWeeks.length} weeks)
                </h3>
                <div className="flex flex-wrap gap-2">
                  {selectedWeeks.sort((a, b) => a - b).map((weekNum) => {
                    const session = SUMMER_2026_SESSIONS.find(s => s.value === weekNum);
                    return (
                      <div key={weekNum} className="bg-white rounded px-3 py-2 text-sm text-gray-700 border border-gray-200">
                        {session ? session.label : `Week ${weekNum}`}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Message */}
              {message && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Additional Message</h3>
                  <div className="bg-white rounded p-3 text-sm text-gray-700 whitespace-pre-wrap">
                    {message}
                  </div>
                </div>
              )}

              {/* Additional Options */}
              {interestedInLateShuttle && (
                <div className="mb-6">
                  <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                    <p className="text-blue-900">🕐 Interested in late shuttle option</p>
                  </div>
                </div>
              )}

              {/* Pricing */}
              {pricing && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Pricing</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Price per week per camper:</span>
                      <span className="font-medium">{formatCurrency(pricing.perStudentPerWeek)}</span>
                    </div>
                    {pricing.discountPercent > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Multi-camper discount ({pricing.discountPercent}% off):</span>
                        <span className="font-medium">Applied</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-700">Number of weeks:</span>
                      <span className="font-medium">{selectedWeeks.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Number of campers:</span>
                      <span className="font-medium">{validStudents.length}</span>
                    </div>
                    <div className="flex justify-between pt-3 border-t text-lg font-bold">
                      <span>Total:</span>
                      <span className="text-shuttlebee-dark">{formatCurrency(pricing.totalPrice)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Next Steps */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">📧 What's Next?</h3>
              <ul className="text-sm text-blue-900 space-y-1 list-disc list-inside">
                <li>Check your email for a detailed quote confirmation</li>
                <li>We'll review your custom route and send you payment information</li>
                <li>Visit your dashboard to track the status of your quote</li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button onClick={() => navigate("/dashboard")} className="w-full">
                View in Dashboard
              </Button>
              <Button variant="outline" onClick={() => navigate("/")} className="w-full">
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-500 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-4 py-1 bg-white/20 rounded-full text-sm font-medium mb-4">
            🚐 Custom Route
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Custom Route Transportation
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Personalized transportation to Summerside Camps at SCH with flexible scheduling.
            Safe, reliable routes with dynamic pricing.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Instruction Note */}
        <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>📋 Note:</strong> Please complete all sections to view your quote and request seats on the shuttle.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-8">
            {/* Camp Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-2xl">🏕️</span>
                  Camp Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-green-900">Summerside Camps at SCH</h3>
                  <p className="text-green-800 text-sm mt-1">
                    8000 Cherokee St, Philadelphia, PA 19118
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Guardian Information */}
            <Card>
              <CardHeader>
                <CardTitle>Guardian Information</CardTitle>
                <CardDescription>Primary contact for this registration</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  label="Full Name"
                  value={guardianInfo.fullName}
                  onChange={(e) => setGuardianInfo({ ...guardianInfo, fullName: e.target.value })}
                  placeholder="John Doe"
                />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    label="Email"
                    type="email"
                    value={guardianInfo.email}
                    onChange={(e) => setGuardianInfo({ ...guardianInfo, email: e.target.value })}
                    placeholder="you@example.com"
                  />
                  <Input
                    label="Phone"
                    type="tel"
                    value={guardianInfo.phone}
                    onChange={(e) => setGuardianInfo({ ...guardianInfo, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Pickup Location */}
            <Card>
              <CardHeader>
                <CardTitle>Pickup Location</CardTitle>
                <CardDescription>
                  Where should we pick up your camper(s)?
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900 space-y-2">
                  <p>
                    Pickup locations are determined based on the addresses of registered participants to create safe, efficient routes.
                  </p>
                  <p>
                    All pickup points must be legal parking areas or designated loading zones where vehicles can safely stop without blocking traffic or violating local regulations.
                  </p>
                  <p>
                    Final pickup locations and times will be confirmed once enrollment is complete and routes are set.
                  </p>
                </div>

                <div className="relative">
                  <Input
                    label="Home Address"
                    value={pickupAddress}
                    onChange={(e) => handlePickupChange(e.target.value)}
                    placeholder="123 Main St, Philadelphia, PA 19103"
                  />
                  {showPickupSuggestions && pickupSuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      {pickupSuggestions.map((suggestion) => (
                        <button
                          key={suggestion.place_id}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 text-sm transition-colors"
                          onClick={() => selectPickupAddress(suggestion)}
                        >
                          {suggestion.description}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {isCalculating && (
                  <div className="text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-600"></div>
                    <p className="text-sm text-gray-600 mt-2">Calculating route...</p>
                  </div>
                )}

                {routeData && (
                  <>
                    <div className="grid grid-cols-2 gap-4 p-4 rounded-xl bg-green-50 border border-green-200">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-900">
                          {routeData.distanceMiles.toFixed(1)} mi
                        </div>
                        <div className="text-sm text-green-700">Distance</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-900">
                          ~{routeData.durationMinutes} min
                        </div>
                        <div className="text-sm text-green-700">Duration</div>
                      </div>
                    </div>
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800">
                        <strong>Note:</strong> This pricing assumes no additional stops along the route.
                        Additional stops may affect the final price.
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Campers */}
            <Card>
              <CardHeader>
                <CardTitle>Campers</CardTitle>
                <CardDescription>Add all children attending camp (up to 6)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {students.map((student, index) => (
                  <div key={student.id} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">Camper {index + 1}</span>
                      {students.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeStudent(student.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          Remove
                        </Button>
                      )}
                    </div>

                    <Input
                      label="Grade"
                      placeholder="e.g., 3rd, 4th, 5th"
                      value={student.grade}
                      onChange={(e) => updateStudent(student.id, "grade", e.target.value)}
                    />

                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={student.needsCarSeat}
                          onChange={(e) => updateStudent(student.id, "needsCarSeat", e.target.checked)}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">Needs car seat</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={student.needsBooster}
                          onChange={(e) => updateStudent(student.id, "needsBooster", e.target.checked)}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">Needs booster seat</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={student.needsAssistance}
                          onChange={(e) => updateStudent(student.id, "needsAssistance", e.target.checked)}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">Needs additional assistance</span>
                      </label>
                    </div>
                  </div>
                ))}

                {students.length < 6 && (
                  <Button variant="outline" onClick={addStudent} className="w-full">
                    + Add Another Camper
                  </Button>
                )}

                {students.length > 1 && (
                  <p className="text-sm text-green-600 font-medium">
                    🎉 Multi-camper discount: {STUDENT_DISCOUNTS[students.length] ?
                      `${Math.round((1 - STUDENT_DISCOUNTS[students.length]) * 100)}% off` :
                      "25% off"}!
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Week Selection */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Select Camp Weeks</CardTitle>
                    <CardDescription>
                      Summer 2026 • 10 weeks available
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={selectAllWeeks}>
                      Select All
                    </Button>
                    <Button variant="ghost" size="sm" onClick={clearWeeks}>
                      Clear
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-3">
                  {SUMMER_2026_SESSIONS.map((session, index) => {
                    const weekNumber = index + 1;
                    const isSelected = selectedWeeks.includes(weekNumber);
                    const hasClosedNotice = weekNumber === 2 || weekNumber === 4;

                    return (
                      <button
                        key={weekNumber}
                        onClick={() => toggleWeek(weekNumber)}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          isSelected
                            ? "border-green-500 bg-green-50"
                            : "border-gray-200 hover:border-green-300 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-gray-900">
                            Week {weekNumber}
                          </span>
                          {isSelected && <span className="text-green-500">✓</span>}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {session.dates}
                          {hasClosedNotice && (
                            <span className="text-blue-600 ml-1">
                              • Closed {weekNumber === 2 ? "June 19th" : "July 3rd"}
                            </span>
                          )}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Late Shuttle Option */}
            <Card>
              <CardContent className="pt-6">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={interestedInLateShuttle}
                    onChange={(e) => setInterestedInLateShuttle(e.target.checked)}
                    className="mt-1 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <div>
                    <span className="font-medium text-gray-900">
                      If a 4:30 PM shuttle becomes available, would you select this option?
                    </span>
                    <p className="text-sm text-gray-600 mt-1">
                      We're gauging interest in a later pickup time to accommodate families who need extended care.
                    </p>
                  </div>
                </label>
              </CardContent>
            </Card>
          </div>

          {/* Price Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <Card className="border-2 border-green-200 bg-gradient-to-br from-white to-green-50">
                <CardHeader className="pb-4">
                  <CardTitle className="text-center">Price Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  {pricing ? (
                    <div className="space-y-4">
                      <div className="text-center pb-4 border-b">
                        <p className="text-sm text-gray-500">Cost Per Student</p>
                        <p className="text-4xl font-bold text-green-600">
                          {formatCurrency(pricing.totalPerStudent)}
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          {formatCurrency(pricing.perStudentPerWeek)}/week per camper, roundtrip
                        </p>
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Route type</span>
                          <span className="font-medium">Custom</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Number of campers</span>
                          <span className="font-medium">{pricing.studentCount}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Weeks selected</span>
                          <span className="font-medium">{pricing.weekCount}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t">
                          <span className="text-gray-600">Route cost/week</span>
                          <span className="font-medium">{formatCurrency(pricing.totalRoutePerWeek)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">÷ {pricing.studentCount} student{pricing.studentCount > 1 ? 's' : ''}</span>
                          <span className="font-medium text-green-600">{formatCurrency(pricing.perStudentPerWeek)}</span>
                        </div>
                        {pricing.discountPercent > 0 && (
                          <div className="flex justify-between text-green-600 pt-2 border-t">
                            <span>Multi-camper savings</span>
                            <span className="font-medium">-{pricing.discountPercent}%</span>
                          </div>
                        )}
                        <div className="flex justify-between pt-2 border-t font-semibold">
                          <span className="text-gray-900">Total for all campers</span>
                          <span className="text-gray-900">{formatCurrency(pricing.totalPrice)}</span>
                        </div>
                      </div>

                      {/* Pricing tiers for routes > 44 minutes */}
                      {routeData && routeData.durationMinutes + 5 > 44 && pricingTiers.length > 6 && (
                        <div className="pt-4 border-t">
                          <p className="text-sm font-medium text-gray-700 mb-3">Cost Per Student by Group Size</p>
                          <div className="space-y-2">
                            {pricingTiers.filter(tier => tier.students >= 4).map((tier) => {
                              const discount = STUDENT_DISCOUNTS[tier.students] || 0.75;
                              const totalRoutePerWeek = tier.pricePerWeek * discount;
                              const perStudentPerWeek = totalRoutePerWeek / tier.students;
                              return (
                                <div key={tier.students} className="flex justify-between items-center text-sm py-2 px-3 rounded bg-white border border-gray-200">
                                  <span className="text-gray-700">{tier.students} student{tier.students > 1 ? 's' : ''}</span>
                                  <span className="font-semibold text-green-600">{formatCurrency(perStudentPerWeek)}/week per camper, roundtrip</span>
                                </div>
                              );
                            })}
                          </div>
                          <p className="text-xs text-gray-500 mt-3">
                            More students = lower cost per student
                          </p>
                        </div>
                      )}

                      {/* Message Field */}
                      <div className="pt-4">
                        <label htmlFor="message" className="block text-sm font-medium text-gray-900 mb-2">
                          Additional Message or Special Requests (Optional)
                        </label>
                        <textarea
                          id="message"
                          rows={4}
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          placeholder="Any special requests, questions, or additional information you'd like us to know..."
                          className="w-full rounded-lg border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-sm px-3 py-2"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          E.g., specific pickup time preferences, sibling coordination, accessibility needs, etc.
                        </p>
                      </div>

                      <div className="pt-4 border-t space-y-3">
                        <label className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            checked={agreedToPrivacyPolicy}
                            onChange={(e) => setAgreedToPrivacyPolicy(e.target.checked)}
                            className="mt-0.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className="text-xs text-gray-700">
                            I agree to the{" "}
                            <a
                              href="/privacy"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-600 hover:underline font-medium"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Privacy Policy
                            </a>{" "}
                            and{" "}
                            <a
                              href="/terms"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-600 hover:underline font-medium"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Terms of Service
                            </a>{" "}
                            <span className="text-red-500">*</span>
                          </span>
                        </label>

                        <label className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            checked={agreedToMarketing}
                            onChange={(e) => setAgreedToMarketing(e.target.checked)}
                            className="mt-0.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className="text-xs text-gray-700">
                            Send me updates and promotions
                          </span>
                        </label>
                      </div>

                      <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting || !pricing || !agreedToPrivacyPolicy}
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        {isSubmitting ? "Submitting..." : "Submit Quote Request"}
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-sm">
                        Enter your address and select weeks to see pricing
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
